/**
 * Hook for PnL distribution data derived from closed positions.
 * Fetches positions, filters to closed, and calculates histogram buckets and stats.
 */

import { useMemo } from 'react';
import useSWR from 'swr';
import { fetchPositions } from '@/services/api';
import {
  calculatePnLDistribution,
  calculateWinRate,
  calculateMeanMedianPnl,
} from '@/utils/performanceCalculations';
import type { PnLBucket } from '@/types/performance';

interface UsePnLDistributionReturn {
  buckets: PnLBucket[];
  winRate: number;
  meanPnl: number;
  medianPnl: number;
  isLoading: boolean;
}

/**
 * Fetch closed positions and derive PnL distribution statistics.
 *
 * @param accountId - The account ID to fetch positions for (null disables fetching)
 * @returns PnL distribution buckets, win rate, mean, median, and loading state
 */
export function usePnLDistribution(
  accountId: string | null
): UsePnLDistributionReturn {
  const { data: positionsData, isLoading } = useSWR(
    accountId ? ['pnlDistribution', accountId] : null,
    () => (accountId ? fetchPositions(accountId) : null),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    }
  );

  const pnlValues = useMemo(() => {
    if (!positionsData?.positions) return [];
    return positionsData.positions
      .filter((p) => p.position_status === 'CLOSED')
      .map((p) => p.realized_pnl);
  }, [positionsData]);

  const buckets = useMemo(() => {
    if (pnlValues.length === 0) return [];
    return calculatePnLDistribution(pnlValues);
  }, [pnlValues]);

  const winRate = useMemo(() => {
    return calculateWinRate(pnlValues);
  }, [pnlValues]);

  const { mean: meanPnl, median: medianPnl } = useMemo(() => {
    return calculateMeanMedianPnl(pnlValues);
  }, [pnlValues]);

  return {
    buckets,
    winRate,
    meanPnl,
    medianPnl,
    isLoading,
  };
}
